import React from 'react';
import {
  Box, Container, Typography, Button, Card, CardContent, Avatar,
  Stack, Chip, Divider, Rating, Grid,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ArticleIcon from '@mui/icons-material/Article';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

/*
 * Conversion-optimized landing page for unauthenticated visitors.
 * All interactive CTAs call the onLogin prop (opens the VK ID login dialog).
 *
 * PLACEHOLDERS the owner must replace with REAL data before launch are marked
 * with the comment tag  ===PLACEHOLDER===  (search the file for it).
 */

const BENEFITS = [
  {
    icon: PeopleIcon,
    title: 'Активная аудитория сообществ',
    text: 'Собирайте тех, кто реально лайкает, комментирует и репостит — а не «мёртвые души».',
  },
  {
    icon: BlurOnIcon,
    title: 'Похожие группы (Lookalike)',
    text: 'Находите сообщества, где сидит ваша целевая аудитория, по пересечению подписок участников.',
  },
  {
    icon: ThumbUpIcon,
    title: 'Анализ активности под постом',
    text: 'Выгружайте всех, кто поставил лайк, репостнул или оставил комментарий под любой записью.',
  },
  {
    icon: ArticleIcon,
    title: 'Парсинг постов и комментариев',
    text: 'Изучайте контент конкурентов и собирайте авторов комментариев — это тёплые лиды.',
  },
  {
    icon: MonitorHeartIcon,
    title: 'Мониторинг сообществ 24/7',
    text: 'Отслеживайте новых вступивших и вышедших — ловите аудиторию конкурентов в момент роста.',
  },
  {
    icon: DownloadIcon,
    title: 'Выгрузка и пересечения',
    text: 'Анализируйте пересечения аудиторий и выгружайте готовые базы в CSV для рекламного кабинета.',
  },
];

// ===PLACEHOLDER=== Replace with REAL stats once available.
const STATS = [
  { value: '1 200+', label: 'специалистов и агентств' },   // ===PLACEHOLDER===
  { value: '4,8 / 5', label: 'средняя оценка' },            // ===PLACEHOLDER===
  { value: '50 млн+', label: 'профилей собрано' },          // ===PLACEHOLDER===
  { value: '< 5 мин', label: 'до первой базы' },            // ===PLACEHOLDER===
];

// ===PLACEHOLDER=== Replace with REAL testimonials (name, role, photo, quote).
const TESTIMONIALS = [
  {
    name: 'Анна К.',
    role: 'Таргетолог',
    quote: 'Раньше собирала аудиторию руками по полдня. Теперь готовая база для VK Ads — за пару минут. CTR вырос ощутимо.',
  },
  {
    name: 'Дмитрий С.',
    role: 'Владелец SMM-агентства',
    quote: 'Lookalike по подпискам — то, чего не хватало. Находим живые сообщества клиентов и не сливаем бюджет на холодную аудиторию.',
  },
  {
    name: 'Марина В.',
    role: 'SMM-специалист',
    quote: 'Мониторинг конкурентов окупил подписку в первую же неделю. Вижу, откуда к ним приходят люди, и забираю эту аудиторию себе.',
  },
];

const PLANS = [
  {
    name: 'Free',
    price: '0 ₽',
    period: 'навсегда',
    features: ['Доступ ко всем инструментам', '10 000 профилей в месяц', 'Выгрузка в CSV'],
    cta: 'Начать бесплатно',
    highlighted: false,
  },
  {
    name: 'Базовый',
    price: '490 ₽',
    period: 'в месяц',
    features: ['30 000 профилей в месяц', 'Все инструменты парсинга', 'Выгрузка в CSV'],
    cta: 'Перейти на Базовый',
    highlighted: false,
  },
  {
    name: 'Pro',
    price: '1 490 ₽',
    period: 'в неделю',
    features: ['100 000 профилей в неделю', 'Мониторинг и автоматизации', 'Lookalike-скоринг', 'Экспорт в VK Ads и myTarget'],
    cta: 'Перейти на Pro',
    highlighted: true,
  },
  {
    name: 'Agency',
    price: '6 990 ₽',
    period: 'в месяц',
    features: ['Безлимит профилей', 'Несколько проектов', 'Приоритетная поддержка'],
    cta: 'Обсудить условия',
    highlighted: false,
  },
];

function SectionHeading({ overline, title, subtitle }) {
  return (
    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 }, maxWidth: 720, mx: 'auto' }}>
      {overline && (
        <Typography
          variant="overline"
          sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1.2 }}
        >
          {overline}
        </Typography>
      )}
      <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, mb: subtitle ? 1.5 : 0, fontSize: { xs: 26, md: 34 } }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: 15, md: 17 } }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

export default function LandingPage({ onLogin }) {
  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
      {/* Top bar */}
      <Box
        sx={{
          position: 'sticky', top: 0, zIndex: 10,
          bgcolor: 'background.default',
          borderBottom: '1px solid', borderColor: 'divider',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Avatar variant="rounded" sx={{ bgcolor: '#6C63FF', width: 36, height: 36, fontSize: 16, fontWeight: 800 }}>
                VK
              </Avatar>
              <Typography variant="h6" fontWeight={800}>VK Parser</Typography>
            </Box>
            <Button variant="outlined" onClick={onLogin} sx={{ fontWeight: 700 }}>
              Войти
            </Button>
          </Box>
        </Container>
      </Box>

      {/* HERO */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 7, md: 12 },
          background: (t) =>
            t.palette.mode === 'dark'
              ? 'radial-gradient(1200px 500px at 50% -10%, rgba(108,99,255,0.22), transparent 70%)'
              : 'radial-gradient(1200px 500px at 50% -10%, rgba(108,99,255,0.14), transparent 70%)',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative' }}>
          <Chip
            label="Парсинг аудитории ВКонтакте"
            color="primary"
            variant="outlined"
            sx={{ mb: 3, fontWeight: 600 }}
          />
          <Typography
            variant="h1"
            fontWeight={900}
            sx={{ fontSize: { xs: 34, sm: 44, md: 56 }, lineHeight: 1.08, mb: 2.5, letterSpacing: -0.5 }}
          >
            Находите клиентов во ВКонтакте,{' '}
            <Box component="span" sx={{
              background: 'linear-gradient(135deg, #6C63FF 0%, #00D4AA 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              а не сливайте рекламный бюджет
            </Box>
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, fontSize: { xs: 16, md: 20 }, maxWidth: 680, mx: 'auto', mb: 4 }}
          >
            Соберите точную целевую аудиторию для рекламы за минуты: активные подписчики сообществ,
            похожие группы и тёплые лиды из комментариев. Для таргетологов, SMM-специалистов и агентств.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 2 }}>
            <Button variant="contained" size="large" onClick={onLogin}
              sx={{ px: 4, py: 1.5, fontSize: 17, fontWeight: 700 }}>
              Начать бесплатно
            </Button>
            <Button variant="outlined" size="large" onClick={onLogin}
              sx={{ px: 4, py: 1.5, fontSize: 17, fontWeight: 700 }}>
              Войти через ВК
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Без карты. Регистрация в один клик через VK ID.
          </Typography>
        </Container>
      </Box>

      {/* STATS strip */}
      <Container maxWidth="lg" sx={{ mb: { xs: 6, md: 10 } }}>
        <Card sx={{ py: { xs: 2, md: 3 } }}>
          <Grid container>
            {STATS.map((s, i) => (
              <Grid item xs={6} md={3} key={i} sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" fontWeight={900} sx={{ color: 'primary.main', fontSize: { xs: 24, md: 32 } }}>
                  {s.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Card>
      </Container>

      {/* BENEFITS */}
      <Container maxWidth="lg" sx={{ mb: { xs: 7, md: 12 } }}>
        <SectionHeading
          overline="Возможности"
          title="Всё для точного таргетинга в одном инструменте"
          subtitle="Шесть инструментов, которые превращают открытые данные ВКонтакте в готовые базы для рекламы."
        />
        <Grid container spacing={3}>
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Avatar variant="rounded" sx={{ bgcolor: 'rgba(108,99,255,0.14)', color: 'primary.main', mb: 2, width: 48, height: 48 }}>
                      <Icon />
                    </Avatar>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1, fontSize: 18 }}>
                      {b.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {b.text}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* HOW IT WORKS */}
      <Box sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', py: { xs: 7, md: 11 }, mb: { xs: 7, md: 12 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            overline="Как это работает"
            title="От ссылки на сообщество до готовой базы — 3 шага"
          />
          <Grid container spacing={3}>
            {[
              { n: '1', t: 'Вставьте ссылку', d: 'Укажите сообщество, пост или список групп, которые хотите проанализировать.' },
              { n: '2', t: 'Запустите парсинг', d: 'Выберите фильтры: активность, гео, пол, возраст. Сервис соберёт нужные профили.' },
              { n: '3', t: 'Выгрузите и запускайте рекламу', d: 'Скачайте базу в CSV и загрузите аудиторию в рекламный кабинет VK Ads.' },
            ].map((step, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 800, width: 44, height: 44 }}>{step.n}</Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: 18, mb: 0.5 }}>{step.t}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{step.d}</Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SOCIAL PROOF */}
      <Container maxWidth="lg" sx={{ mb: { xs: 7, md: 12 } }}>
        <SectionHeading
          overline="Отзывы"
          title="Нам доверяют маркетологи и агентства"
          subtitle="Реальные результаты тех, кто перестал работать с холодной аудиторией."
        />
        <Grid container spacing={3}>
          {TESTIMONIALS.map((t, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <FormatQuoteIcon sx={{ color: 'primary.main', fontSize: 36, mb: 1 }} />
                  <Typography variant="body1" sx={{ mb: 2, flexGrow: 1, lineHeight: 1.6 }}>
                    «{t.quote}»
                  </Typography>
                  <Rating value={5} readOnly size="small" sx={{ mb: 1.5 }} />
                  <Divider sx={{ mb: 1.5 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', fontWeight: 700 }}>{t.name[0]}</Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>{t.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.role}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* PRICING TEASER */}
      <Container maxWidth="lg" sx={{ mb: { xs: 7, md: 12 } }}>
        <SectionHeading
          overline="Тарифы"
          title="Начните бесплатно — платите, когда вырастете"
          subtitle="Попробуйте все инструменты на бесплатном тарифе. Переходите на Pro, когда понадобятся большие объёмы."
        />
        <Grid container spacing={3} alignItems="stretch" justifyContent="center">
          {PLANS.map((p, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card sx={{
                height: '100%',
                position: 'relative',
                border: p.highlighted ? '2px solid' : '1px solid',
                borderColor: p.highlighted ? 'primary.main' : 'divider',
                boxShadow: p.highlighted ? '0 8px 30px rgba(108,99,255,0.35)' : undefined,
              }}>
                {p.highlighted && (
                  <Chip label="Популярный" color="primary" size="small"
                    sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 700 }} />
                )}
                <CardContent sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>{p.name}</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography component="span" variant="h4" fontWeight={900}>{p.price}</Typography>
                    <Typography component="span" variant="body2" color="text.secondary"> {p.period}</Typography>
                  </Box>
                  <Stack spacing={1} sx={{ mb: 3, flexGrow: 1 }}>
                    {p.features.map((f, j) => (
                      <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
                        <Typography variant="body2">{f}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Button
                    fullWidth
                    variant={p.highlighted ? 'contained' : 'outlined'}
                    size="large"
                    onClick={onLogin}
                    sx={{ fontWeight: 700 }}
                  >
                    {p.cta}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ mt: 2 }}>
          {/* ===PLACEHOLDER=== Точные цены и лимиты см. в разделе «Тарифы» после входа. */}
          Точные лимиты и цены — в разделе «Тарифы» после регистрации.
        </Typography>
      </Container>

      {/* TRUST badges */}
      <Container maxWidth="lg" sx={{ mb: { xs: 7, md: 12 } }}>
        <Grid container spacing={3} justifyContent="center">
          {[
            { icon: BoltIcon, t: 'Быстрый старт', d: 'Первая база за пару минут после входа.' },
            { icon: VerifiedUserIcon, t: 'Только открытые данные', d: 'Работаем с публичной информацией ВКонтакте.' },
            { icon: DownloadIcon, t: 'Готово для VK Ads', d: 'Выгрузка в форматах для рекламного кабинета.' },
          ].map((it, i) => {
            const Icon = it.icon;
            return (
              <Grid item xs={12} sm={4} key={i}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Icon sx={{ color: 'primary.main', fontSize: 32 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>{it.t}</Typography>
                    <Typography variant="body2" color="text.secondary">{it.d}</Typography>
                  </Box>
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* FINAL CTA */}
      <Container maxWidth="md" sx={{ mb: { xs: 7, md: 12 } }}>
        <Card sx={{
          textAlign: 'center',
          p: { xs: 4, md: 7 },
          background: 'linear-gradient(135deg, rgba(108,99,255,0.18) 0%, rgba(0,212,170,0.14) 100%)',
          border: '1px solid', borderColor: 'primary.main',
        }}>
          <Typography variant="h4" fontWeight={900} sx={{ mb: 2, fontSize: { xs: 26, md: 36 } }}>
            Соберите свою первую целевую аудиторию сегодня
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 560, mx: 'auto', fontSize: { xs: 15, md: 17 } }}>
            Регистрация занимает один клик через VK ID. Бесплатный тариф — без карты и обязательств.
          </Typography>
          <Button variant="contained" size="large" onClick={onLogin}
            sx={{ px: 5, py: 1.5, fontSize: 18, fontWeight: 700 }}>
            Начать бесплатно
          </Button>
        </Card>
      </Container>

      {/* FOOTER */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Avatar variant="rounded" sx={{ bgcolor: '#6C63FF', width: 30, height: 30, fontSize: 13, fontWeight: 800 }}>VK</Avatar>
              <Typography variant="subtitle1" fontWeight={800}>VK Parser</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} VK Parser · vk-pars.ru · Сервис не аффилирован с ВКонтакте
            </Typography>
            <Button variant="text" onClick={onLogin} sx={{ fontWeight: 700 }}>
              Войти
            </Button>
          </Box>
          <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 3 }, justifyContent: 'center' }}>
            {[
              ['Публичная оферта', '/legal/oferta.html'],
              ['Политика обработки персональных данных', '/legal/privacy.html'],
              ['Пользовательское соглашение', '/legal/terms.html'],
            ].map(([label, href]) => (
              <Typography key={href} component="a" href={href} target="_blank" rel="noopener noreferrer"
                variant="body2" sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline', color: 'text.primary' } }}>
                {label}
              </Typography>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
